<script setup lang="ts">
import merge from 'lodash-es/merge';
import { useWebsitesStore } from '../../../pages/cms/management/websites/_runtime/stores/websites';
import type { ListItemModel } from '../../../pages/cms/modules/videos/_runtime/services/VideosService';
import VideosService from '../../../pages/cms/modules/videos/_runtime/services/VideosService';

interface FormSettings
{
    settings: any;
}

defineOptions({
    name: 'WidgetVideosSettings'
});

const emit = defineEmits<{
    (e: 'update:modelValue', payload: any): void;
}>();

const props = defineProps<{
    modelValue: any;
}>();

const { $t } = useLocalization();

const module = ref('videos');
const defaults = ref<any>({
    nameIsVisible: true,
    pageId: null,
    relatedVideos: []
});
const videoId = ref<number>(null);
const related = ref<ListItemModel[]>([]);

const form = computed<FormType<FormSettings>>(() =>
{
    return props.modelValue;
});

const current = computed<CurrentWebsite>(() =>
{
    return useWebsitesStore().current;
});

function triggerModel(): void
{
    emit('update:modelValue', form.value);
}

async function validate(): Promise<void>
{
    form.value.validate(schema => ({
        settings: schema.object({
            pageId: schema.string().nullable().required($t('[[[Wybranie strony jest wymagane]]]'))
        })
    }));
}

async function fetchVideo(id: number): Promise<ListItemModel>
{
    const response = await VideosService.fetchAutocompleteModel(id);

    return response || null;
}

async function searchVideos(query: string, limit: number): Promise<ListItemModel[]>
{
    return await VideosService.getAutocompleteList(query, limit);
}

async function addRelated(): Promise<void>
{
    if (videoId.value == null)
        return;

    const response = await VideosService.fetchAutocompleteModel(videoId.value);

    if (related.value.find(x => x.originId === response.originId) === undefined &&
      form.value.settings.relatedVideos.find((x: any) => x === response.originId) === undefined)
    {
        related.value.push(response);
        form.value.settings.relatedVideos.push(response.originId);
    }

    videoId.value = null;
}

function removeRelated(originId: number): void
{
    related.value = related.value.filter(x => x.originId != originId);
    form.value.settings.relatedVideos = form.value.settings.relatedVideos.filter((x: any) => x != originId);
}

async function loadRelated(id: number): Promise<boolean>
{
    if (id === 0)
        return true;

    try
    {
        related.value = await VideosService.getRelatedFromWidget(id);
    }
    catch (ex)
    {
        return false;
    }

    return true;
}

watch(() => form.value.settings, () =>
{
    triggerModel();
}, { deep: true });

onBeforeMount(async (): Promise<void> =>
{
    await wait(() => current.value.website);
    form.value.settings = merge(defaults.value, form.value.settings);
    loadRelated(form.value.id);
});

defineExpose({
    validate
});
</script>

<template>
    <IdeoFormGroup>
        <IdeoFormCheckbox v-model="form.settings.nameIsVisible">
            {{ $t('[[[Wyświetlaj nazwę bloczka]]]') }}
        </IdeoFormCheckbox>
    </IdeoFormGroup>
    <IdeoFormGroup :label="$t('[[[Wybór strony]]]')" :invalid-feedback="form.$errors.first('settings.pageId')" :state="form.$errors.state('settings.pageId')" required>
        <EditoPageSelector v-model="form.settings.pageId" :module="module" :preview="false" label="" />
    </IdeoFormGroup>

    <IdeoFormGroup :label="$t('[[[Wybierz wideo]]]')">
        <div class="d-flex flex-column flex-md-row">
            <div class="flex-grow-1">
                <IdeoAutocomplete
                    v-model="videoId"
                    name="videoId"
                    track-by="id"
                    :placeholder="$t('[[[zacznij pisać aby wyszukać wideo...]]]')"
                    :fetch="(id: number) => fetchVideo(id)"
                    :search="(query: string, limit: number) => searchVideos(query, limit)"
                >
                    <template #selected="{ option }">
                        <div class="text-wrap">
                            {{ option.title }}
                        </div>
                    </template>
                    <template #option="{ option }">
                        <div class="text-wrap">
                            {{ option.title }}
                        </div>
                    </template>
                </IdeoAutocomplete>
            </div>
            <div class="d-flex align-items-stretch">
                <button class="btn btn-secondary text-nowrap mt-2 mt-md-0 ms-md-2" @click.prevent="addRelated()">
                    <i class="fas fa-plus" />
                </button>
            </div>
        </div>
    </IdeoFormGroup>

    <IdeoFormGroup :label="$t('[[[Powiązane wideo]]]')">
        <div class="list-group">
            <div v-for="item in related" :key="item.id" class="list-group-item list-group-item-action p-2 d-flex">
                <span class="me-2">
                    <i class="fad fa-fw fa-browser text-primary" />
                </span>
                <span class="me-auto">
                    <span class="d-block" :title="$t('[[[Powiązane wideo]]]')">
                        <b>{{ item.title }}</b>
                    </span>
                </span>
                <span class="d-flex align-items-center">
                    <IdeoButton :id="`btn-delete-related-${item.originId}`" variant="danger" size="sm" pill :title="$t('[[[Usuń powiązanie]]]')">
                        <i class="fas fa-trash-alt" />
                    </IdeoButton>
                    <Confirmation :message="$t('[[[Potwierdzenie usunięcia]]]')" :target="`btn-delete-related-${item.originId}`" :value="item.id" @confirm="removeRelated(item.originId)" />
                </span>
            </div>
            <div v-if="related.length == 0" class="list-group-item list-group-item-light text-center">
                {{ $t('[[[Brak powiązań]]]') }}
            </div>
        </div>
    </IdeoFormGroup>
</template>
